import React, { useRef } from 'react';
import { cn } from '../../lib/cn';

interface ColorPickerFieldProps {
  label: string;
  value: string;
  presets: string[];
  onChange: (value: string) => void;
}

function isValidHex(hex: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

export function ColorPickerField({
  label,
  value,
  presets,
  onChange,
}: ColorPickerFieldProps) {
  const colorInputRef = useRef<HTMLInputElement>(null);
  const isValid = isValidHex(value);

  function handleHexInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    // Always update the text input immediately so the user can type
    onChange(raw);
  }

  function handleColorNativeChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-700">{label}</label>

      <div className="flex items-center gap-2">
        {/* Color swatch preview */}
        <div
          className="w-8 h-8 rounded-md border border-neutral-300 shrink-0 cursor-pointer"
          style={{ backgroundColor: isValid ? value : '#cccccc' }}
          onClick={() => colorInputRef.current?.click()}
          title="Cliquez pour ouvrir le sélecteur de couleur"
          aria-label={`Aperçu couleur ${label}`}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') colorInputRef.current?.click();
          }}
        />

        {/* Hex text input */}
        <input
          type="text"
          value={value}
          onChange={handleHexInputChange}
          placeholder="#000000"
          maxLength={7}
          className={cn(
            'w-28 h-8 px-2 text-sm rounded-md border bg-white font-mono',
            'focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors',
            !isValid && value.length > 0
              ? 'border-red-400 text-red-600'
              : 'border-neutral-300 text-neutral-900',
          )}
          aria-label={`Valeur hexadécimale ${label}`}
          aria-invalid={!isValid && value.length > 0}
        />

        {/* Native color picker (hidden, triggered by swatch click) */}
        <input
          ref={colorInputRef}
          type="color"
          value={isValid ? value : '#000000'}
          onChange={handleColorNativeChange}
          className="sr-only"
          aria-hidden
          tabIndex={-1}
        />
      </div>

      {/* Preset swatches */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            title={preset}
            aria-label={`Preset couleur ${preset}`}
            className={cn(
              'w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-cime-primary',
              value.toLowerCase() === preset.toLowerCase()
                ? 'border-cime-primary scale-110'
                : 'border-neutral-200',
            )}
            style={{ backgroundColor: preset }}
          />
        ))}
      </div>
    </div>
  );
}
